import React, { useEffect, useState } from 'react';
import './Styles.css';
import { formatMessage } from '../../Localization/formatMessage';
import Messages from '../../Localization/Messages';
import { ServiceClient } from '../../Services/ServiceClient';
import { PresentationMode } from '../../Enums/PresentationMode';
import LoaderComponent from '../../Elements/ids/Loader';
import { useNavigate, useParams } from 'react-router-dom';
import {
  calculateBrokerCostRatesToBeShown,
  convertApiResultForApp,
  getRate,
  updateScrollbarWidthAndLocation,
  updateScrollBarVisibility,
  updateBrokerCostRateListAfterSave,
  getNewDefaultBcr,
  convertApiCustomerListForApp,
} from './Functions';
import { BrokerCostType } from '../../Enums/BrokerCostType';
import ExpandedView from './ExpandedView';
import { Ui24ArrowSmDown, Ui16Check, Ui16Plus, Ui24ArrowSmUp } from '@ids/react-icons';
import { IconButton, IconButtonType, IconButtonSize } from '@ids/react-icon-button';
import CustomerRateLookup from './CustomerRateLookup';
import EditBrokerCostRate from './EditBrokerCostRate';
import ErrorMessage from '../../Elements/ErrorMessage';
import { createServerErrorMessage } from '../../Helpers/FormInputHelper';
import { useGlobalContext } from '../../context/GlobalContext';
import BreadcrumbRow from '../../Elements/Breadcrumbs';

const scrollbarId = 'scrollbar';
const brokerCostTableContainerId = 'brokerCostTableContainer';

export const bcrModalModeEnum = {
  createDefaultRate: 'CreateDefaultRate',
  editDefaultRate: 'EditDefaultRate',
  newCustomerRate: 'NewCustomerRate',
  editCustomerRate: 'EditCustomerRate',
  none: 'none',
};

const CommissionPage = () => {
  const navigate = useNavigate();
  const { addBreadcrumbs } = useGlobalContext();
  const { agentCode } = useParams();
  const [initialCustomers, setInitialCustomers] = useState(null);
  const [brokerCostRates, setBrokerCostRates] = useState([]);
  const [pageMode, setPageMode] = useState(PresentationMode.Loading);
  const [expandedRate, setExpandedRate] = useState(null);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [modalMode, setModalMode] = useState(bcrModalModeEnum.none);
  const [error, setError] = useState(null);

  /**
   * This effect controls custom horizontal scrollbar.
   * Dynamically changes its size based on window and content width and hides it if not needed.
   * Custom solution was needed so the scrollbar would always be visible at the top or bottom of the result list
   */
  useEffect(() => {
    const bcrTableContainer = document.getElementById(brokerCostTableContainerId);
    const scrollbar = document.getElementById(scrollbarId);
    const pageContainer = document.getElementById('commissionPageContainer');

    if (!bcrTableContainer || !scrollbar || !pageContainer) return;

    // handles scrolling
    const handleListScroll = () => {
      scrollbar.scrollLeft = bcrTableContainer.scrollLeft;
    };
    const handleProxyScroll = () => {
      bcrTableContainer.scrollLeft = scrollbar.scrollLeft;
    };

    scrollbar.style.left = `${bcrTableContainer.x}px`;

    const pageContainerObserver = new ResizeObserver(entries => {
      updateScrollBarVisibility(scrollbar, bcrTableContainer);
      updateScrollbarWidthAndLocation(scrollbar, bcrTableContainer, entries[0]);
    });

    // event listeners for scrolling and window resize
    pageContainerObserver.observe(pageContainer);
    bcrTableContainer.addEventListener('scroll', handleListScroll);
    scrollbar.addEventListener('scroll', handleProxyScroll);

    return () => {
      pageContainerObserver.disconnect();
      bcrTableContainer.removeEventListener('scroll', handleListScroll);
      scrollbar.removeEventListener('scroll', handleProxyScroll);
    };
  }, [brokerCostRates]);

  useEffect(() => {
    addBreadcrumbs([
      {
        trackingName: 'teamPage',
        link: `/team/view/${agentCode.split('-')[0]}`,
        title: formatMessage(Messages.breadcrumb_teamPage),
      },
      {
        trackingName: 'commissionPage',
        link: `/team/commission/${agentCode}`,
        title: formatMessage(Messages.breadcrumb_commissionPage),
      },
    ]);

    ServiceClient.getCustomersWithDeviatedBrokerCost(
      agentCode,
      customersDto => {
        setInitialCustomers(convertApiCustomerListForApp(customersDto));
        ServiceClient.getAllCurrentBrokerRates(
          agentCode,
          rates => {
            if (rates.length > 0) {
              setPageMode(PresentationMode.View);
              setBrokerCostRates(convertApiResultForApp(rates, customersDto));
            } else {
              setModalMode(bcrModalModeEnum.createDefaultRate);
              setPageMode(PresentationMode.Add);
              setBrokerCostRates([getNewDefaultBcr(agentCode)]);
            }
          },
          () => {
            setError(createServerErrorMessage(error).message);
            setPageMode(PresentationMode.Error);
          }
        );
      },
      () => {
        setError(createServerErrorMessage(error).message);
        setPageMode(PresentationMode.Error);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openModalForNewCustomer = () => {
    setModalMode(bcrModalModeEnum.newCustomerRate);
    setPageMode(PresentationMode.Add);
    setExpandedRate(null);
  };

  return (
    <div className="commission-page_container" id="commissionPageContainer">
      <BreadcrumbRow />
      <div className="commission-page_header-container">
        <h1>{agentCode.toUpperCase()}</h1>
        {pageMode !== PresentationMode.Loading && (
          <>
            <IconButton type={IconButtonType.FILLED} size={IconButtonSize.MEDIUM} onClick={openModalForNewCustomer}>
              <Ui16Plus />
            </IconButton>

            <CustomerRateLookup
              customerList={initialCustomers}
              filteredCustomers={filteredCustomers}
              setFilteredCustomers={setFilteredCustomers}
              setExpandedRate={setExpandedRate}
              openModalForNewCustomer={openModalForNewCustomer}
            />
          </>
        )}
      </div>

      {pageMode === PresentationMode.Loading ? (
        <LoaderComponent />
      ) : pageMode === PresentationMode.Error ? (
        <ErrorMessage message={error} />
      ) : (
        <>
          <div className="commission-page_table-container" id={brokerCostTableContainerId}>
            <table className="commission-page_table" cellSpacing="0" cellPadding="0">
              <thead>
                <tr>
                  <th className="commission-page_table-customer-number">
                    {formatMessage(Messages.commissionPage_waypointNumber)}
                  </th>
                  <th className="commission-page_table-customer-name">
                    {formatMessage(Messages.commissionPage_customer)}
                  </th>
                  <th>{formatMessage(Messages.commissionPage_organizationNumber)}</th>
                  <th>{formatMessage(Messages.commissionPage_brokerCostAcceptance)}</th>
                  <th>{formatMessage(Messages.commissionPage_personalAcceptance)}</th>
                  <th>{formatMessage(Messages.commissionPage_motorAcceptance)}</th>
                  <th>{formatMessage(Messages.commissionPage_rateTypeProperty)}</th>
                  <th>{formatMessage(Messages.commissionPage_rateTypeLiability)}</th>
                  <th>{formatMessage(Messages.commissionPage_rateTypePerson)}</th>
                  <th>{formatMessage(Messages.commissionPage_rateTypeTransport)}</th>
                  <th>{formatMessage(Messages.commissionPage_rateTypeCasco)}</th>
                  <th>{formatMessage(Messages.commissionPage_rateTypePersonalCommission)}</th>
                  <th>{formatMessage(Messages.commissionPage_rateTypeMotorCommission)}</th>
                </tr>
              </thead>
              <tbody>
                {calculateBrokerCostRatesToBeShown(brokerCostRates, filteredCustomers).map(brokerCostRate => {
                  const isOpen = expandedRate === brokerCostRate?.customerNumber;
                  return (
                    <React.Fragment key={`value-row-${brokerCostRate?.customerNumber}`}>
                      <tr
                        className={`${
                          brokerCostRate?.inheritCommissionHandling ? 'commission-page_table-row-inherited' : ''
                        }
                        commission-page_table-row`}
                        onClick={() => {
                          setExpandedRate(isOpen ? null : brokerCostRate?.customerNumber);
                        }}>
                        <td>{brokerCostRate?.customerNumber ?? '-'}</td>
                        <td>{brokerCostRate?.customerName ?? '-'}</td>
                        <td>{brokerCostRate?.legalNumber ?? '-'}</td>
                        <td>{brokerCostRate?.useBrokerCost && <Ui16Check />}</td>
                        <td>{brokerCostRate?.usePersonalCommission && <Ui16Check />}</td>
                        <td>{brokerCostRate?.useMotorCommission && <Ui16Check />}</td>
                        <td>{getRate(brokerCostRate?.rates, BrokerCostType.Property)}</td>
                        <td>{getRate(brokerCostRate?.rates, BrokerCostType.Liability)}</td>
                        <td>{getRate(brokerCostRate?.rates, BrokerCostType.Person)}</td>
                        <td>{getRate(brokerCostRate?.rates, BrokerCostType.Transport)}</td>
                        <td>{getRate(brokerCostRate?.rates, BrokerCostType.Casco)}</td>
                        <td>{getRate(brokerCostRate?.rates, BrokerCostType.PersonalCommission)}</td>
                        <td>
                          {getRate(brokerCostRate?.rates, BrokerCostType.MotorCommission)}
                          <span className="commission-page_table-expand-icon">
                            {isOpen ? <Ui24ArrowSmUp /> : <Ui24ArrowSmDown />}
                          </span>
                        </td>
                      </tr>
                      {isOpen && (
                        <ExpandedView
                          brokerCostRate={brokerCostRate}
                          openModal={() => {
                            if (brokerCostRate.customerNumber) {
                              setModalMode(bcrModalModeEnum.editCustomerRate);
                            } else {
                              setModalMode(bcrModalModeEnum.editDefaultRate);
                            }
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {modalMode !== bcrModalModeEnum.none && (
              <EditBrokerCostRate
                bcr={brokerCostRates.find(bcr => bcr.customerNumber === expandedRate) ?? getNewDefaultBcr(agentCode)}
                modalMode={modalMode}
                onSave={newRate => {
                  updateBrokerCostRateListAfterSave(newRate, modalMode, brokerCostRates, setBrokerCostRates);
                  setExpandedRate(null);
                  setPageMode(PresentationMode.View);
                  setModalMode(bcrModalModeEnum.none);
                }}
                defaultBcr={brokerCostRates[0]}
                closeEditView={() => {
                  if (modalMode === bcrModalModeEnum.createDefaultRate) {
                    navigate(`/team/view/${agentCode.split('-')[0]}`);
                  }
                  setModalMode(bcrModalModeEnum.none);
                }}
                existingCustomers={initialCustomers}
              />
            )}
          </div>

          <div className="commission-page_scrollbar-container" id={scrollbarId}>
            <div className="commission-page_scrollbar-content"></div>
          </div>
        </>
      )}
    </div>
  );
};

export default CommissionPage;
